// React & Hooks

// Custom Hooks
import { useQuery } from '@tanstack/react-query';
// External Libraries & Styles
import AirDatepicker, { type AirDatepickerPosition } from 'air-datepicker';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Tabulator } from 'react-tabulator/lib/types/TabulatorTypes';
import { useUserManagement } from '@/domains/system-management/hooks/useUserManagement';
import { smartCityApi } from '@/domains/traffic-monitoring/apis/smart-city';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
// Components
import {
  type ColumnDefinition,
  ReactTabulator,
} from '@/shared/components/ui/LocalizedTabulator/LocalizedTabulator';
import LogList from '@/shared/components/ui/LogList/LogList';
import SearchSelect from '@/shared/components/ui/SearchSelect/SearchSelect';
import Select from '@/shared/components/ui/Select/Select';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import 'air-datepicker/air-datepicker.css';
import localeEn from 'air-datepicker/locale/en';
import localeId from 'air-datepicker/locale/id';
import localeKo from 'air-datepicker/locale/ko';
import dayjs from 'dayjs';
// Types
import type {
  ButtonState,
  Params,
  User,
  UserListResponse,
  UserRequest,
} from '@/domains/system-management/types/user';
// Utilities
import { formatFullDateTimeDDMMYYYY } from '@/shared/utils/date';
import { zoomSafeDatepickerPosition } from '@/shared/utils/datepickerPosition';
import Common from '@/shared/utils/standard';

// Akun MOBILE = ID akun murni angka (health_care_facility_id). Di luar
// komponen supaya referensinya stabil untuk useMemo di bawah.
const isMobileAccount = (accountId?: string | null) => /^[0-9]+$/.test(String(accountId ?? ''));

const UserManagement = () => {
  const { t, i18n } = useTranslation();

  // Satu endpoint /users berisi DUA jenis akun: akun WEB (ID string, mis.
  // "davidUser") dan akun MOBILE untuk aplikasi kru ambulance (ID murni
  // angka = health_care_facility_id, mis. "109"). Filter User Type (Web /
  // Mobile) di bar filter memilih jenis yang tampil; pemisahnya derivatif dari
  // bentuk ID, backend tidak berubah.
  // Dideklarasikan paling atas karena columnsHistory di bawah memakainya.
  const [userKind, setUserKind] = useState<'web' | 'mobile'>('web');

  // User Type Web - Jenis Akun. General (dinas, Dishub, super user / root)
  // tidak punya rumah sakit; nilainya tetap 'internal', sama dengan
  // account_type dari API login. Rumah Sakit wajib memilih fasilitas yang
  // ditautkan lewat health_care_facility.user_id_website. Bukan kolom DB:
  // diturunkan dari ada-tidaknya tautan saat user dibuka.
  const [webAccountType, setWebAccountType] = useState<'internal' | 'hospital'>('internal');

  const commonColumnSettings = {
    hozAlign: 'center' as const,
    headerHozAlign: 'center' as const,
    resizable: false,
  };

  const columnsHistory = [
    {
      title: t('cmn > no'),
      formatter: (cell: Tabulator.CellComponent) => {
        const row = cell.getRow();
        const page = row.getTable().getPage();
        const pageSize = row.getTable().getPageSize();
        return (Number(page) - 1) * Number(pageSize) + row.getPosition(true);
      },
      width: 65,
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      // User Type Mobile: ID akun = health_care_facility_id, jadi headernya ikut dan
      // bisa diurutkan naik/turun secara numerik.
      title: userKind === 'mobile' ? t('user > health care facility id') : t('user > id'),
      field: 'account_id',
      widthGrow: 1,
      // Lebar minimum supaya judul tidak terpotong oleh panah sort; tristate =
      // klik berurutan naik -> turun -> tanpa urutan.
      minWidth: userKind === 'mobile' ? 240 : 120,
      headerSort: userKind === 'mobile',
      headerSortTristate: true,
      sorter: userKind === 'mobile' ? 'number' : 'string',
      ...commonColumnSettings,
    },
    {
      // User Type Mobile: nama fasilitas dari backend (find_users), dicari lewat
      // health_care_facility_id = ID akun.
      title: t('healthcare > healthcare name'),
      field: 'health_care_facility_name',
      widthGrow: 2,
      minWidth: 220,
      headerSort: true,
      headerSortTristate: true,
      sorter: 'string',
      visible: userKind === 'mobile',
      ...commonColumnSettings,
    },
    {
      title: t('user > name'),
      field: 'name',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: t('user > email'),
      field: 'email',
      widthGrow: 2,
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: t('user > phone no'),
      field: 'phone_no',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      // User Type Web: jenis akun, diturunkan dari ada-tidaknya tautan rumah sakit
      // (sama dengan dropdown Jenis Akun di form).
      title: t('user > account type'),
      field: 'health_care_facility_id',
      widthGrow: 1,
      // "Healthcare Facility" / "Fasilitas Kesehatan" terpotong di lebar default.
      minWidth: 160,
      headerSort: false,
      visible: userKind === 'web',
      formatter: (cell: Tabulator.CellComponent) => {
        const row = cell.getRow().getData() as UserListResponse;
        return row.health_care_facility_id == null
          ? t('user > account type general')
          : t('user > account type hospital');
      },
      ...commonColumnSettings,
    },
    {
      // User Type Web: rumah sakit yang ditautkan ke akun web ini
      // (health_care_facility.user_id_website), format sama dengan dropdown.
      title: t('user > health care facility id'),
      field: 'health_care_facility_id',
      widthGrow: 2,
      headerSort: false,
      visible: userKind === 'web',
      formatter: (cell: Tabulator.CellComponent) => {
        const row = cell.getRow().getData() as UserListResponse;
        if (row.health_care_facility_id == null) return '-';
        const text = `${row.health_care_facility_id} - ${row.health_care_facility_name ?? ''}`;
        return text.replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);
      },
      ...commonColumnSettings,
    },
    {
      title: t('021'),
      field: 'position',
      widthGrow: 2,
      headerSort: false,
      ...commonColumnSettings,
    },
  ];

  const tbRef = useRef<Tabulator | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const idRef = useRef<HTMLInputElement | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const birthRef = useRef<HTMLInputElement | null>(null);

  // local refs to keep latest values inside callbacks
  const hasChangesUpdateRef = useRef<boolean>(false);
  const hasChangesCreateRef = useRef<boolean>(false);
  const isNewClickedRef = useRef<boolean>(false);
  const selectedUserRef = useRef<{ id?: string | null }>({ id: null });

  const [disabledForm, setDisabledForm] = useState<boolean>(true);
  const [disabledId, setDisabledId] = useState<boolean>(true);
  const [hasChangesUpdate, setHasChangesUpdate] = useState<boolean>(false);
  const [hasChangesCreate, setHasChangesCreate] = useState<boolean>(false);
  const [isRequired, setIsRequired] = useState<boolean>(false);
  const [newId, setNewId] = useState<string | null>(null);
  const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<{
    id?: string | null;
  }>({ id: null });

  const [formValues, setFormValues] = useState<Partial<UserRequest>>({});

  const emptyDetail = () => {
    setFormValues(
      (prev) => Object.fromEntries(Object.keys(prev).map((key) => [key, ''])) as UserRequest
    );
  };

  // Button states
  const [buttonState, setButtonState] = useState<ButtonState>({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  const disableAllButtons = () =>
    setButtonState({ confirm: true, cancel: true, delete: true, create: true });

  const enableInitialButtons = () => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prev) => ({ ...prev, create: false }));
  };
  const enableUpdateButtons = () => {
    disableAllButtons();
    setButtonState((prev) => ({
      ...prev,
      cancel: false,
      delete: false,
      create: false,
    }));
  };
  const enableRegisterButtons = () => {
    disableAllButtons();
    setButtonState((prev) => ({ ...prev, cancel: false }));
  };

  // CRUD callbacks
  const reloadCallback = () => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    setIsRequired(false);
    emptyDetail();
    setDisabledForm(true);
    setDisabledId(true);
    setHasChangesUpdate(false);
    setSelectedUser({ id: null });
  };

  const createCallback = () => {
    tbRef.current?.deselectRow();
    enableInitialButtons();
    emptyDetail();
    setDisabledForm(true);
    setIsRequired(false);
    setDisabledId(true);
    setHasChangesCreate(false);
    setSelectedUser({ id: null });
    setNewId(null);
  };

  const updateCallback = () => {
    enableUpdateButtons();
    setIsRequired(false);
    setHasChangesUpdate(false);
  };

  // API hooks
  const [queryParams, setQueryParams] = useState<Params>({
    deletion: '001002',
  });

  const [optionParams] = useState<string>('upper-code=021&upper-code=001&upper-code=ORG');

  const { useUsers, useUser, useCreateUser, useUpdateUser, useDeleteUser } =
    useUserManagement(queryParams);

  const { data: usersData } = useUsers();

  // Pembagian Web / Mobile (filter User Type) dari satu response /users yang sama.
  // Dimemo: react-tabulator membangun ulang seluruh tabel setiap kali
  // referensi `data` berubah. Tanpa memo, setiap ketikan di form (re-render)
  // menghasilkan array baru dari filter(), tabel dibongkar-pasang, dan
  // halaman melompat ke atas.
  const webUsers = useMemo(
    () => (usersData || []).filter((u) => !isMobileAccount(u.account_id)),
    [usersData]
  );
  const mobileUsers = useMemo(
    () => (usersData || []).filter((u) => isMobileAccount(u.account_id)),
    [usersData]
  );
  // Tab Account Type di atas tabel (hanya saat User Type = Website): All /
  // General / Healthcare Facility, diterapkan di sisi klien dari tautan rumah
  // sakit yang sudah ada di data list.
  const [accountTypeFilter, setAccountTypeFilter] = useState<'All' | 'internal' | 'hospital'>(
    'All'
  );
  const accountTypeTabs: { value: 'All' | 'internal' | 'hospital'; label: string }[] = [
    { value: 'All', label: t('cmn > all') },
    { value: 'internal', label: t('user > account type general') },
    { value: 'hospital', label: t('user > account type hospital') },
  ];
  const displayedUsers = useMemo(() => {
    if (userKind !== 'web') return mobileUsers;
    if (accountTypeFilter === 'internal')
      return webUsers.filter((u) => u.health_care_facility_id == null);
    if (accountTypeFilter === 'hospital')
      return webUsers.filter((u) => u.health_care_facility_id != null);
    return webUsers;
  }, [userKind, webUsers, mobileUsers, accountTypeFilter]);

  // Filter User Type (Website / Mobile) di bar filter. Tab Account Type di atas
  // tabel hanya ada untuk Website - akun mobile tidak punya jenis lain.
  const userTypeOptions = [
    { value: 'web', label: t('user > user type web') },
    { value: 'mobile', label: t('user > user type mobile') },
  ];

  const handleKindChange = (kind: 'web' | 'mobile') => {
    if (kind === userKind) return;
    const switchKind = () => {
      setUserKind(kind);
      // Filter turunan milik User Type sebelumnya tidak berlaku lagi.
      setAccountTypeFilter('All');
      // Ganti User Type = konteks baru: lepas seleksi + kosongkan form supaya
      // tidak ada sisa editan akun dari jenis sebelumnya. reloadCallback tidak
      // menyentuh flag create dan newId: tanpa dua baris terakhir, user yang
      // baru dibuat terpilih lagi saat tabel dibangun ulang, dan klik row
      // berikutnya meminta konfirmasi untuk form yang sebenarnya sudah kosong.
      reloadCallback();
      setHasChangesCreate(false);
      setNewId(null);
    };
    // Sama seperti New / pilih row / Cancel: editan yang belum disimpan tidak
    // dibuang tanpa konfirmasi. Select User Type terkontrol oleh userKind, jadi
    // kalau dibatalkan pilihannya kembali sendiri.
    if (hasChangesCreate || hasChangesUpdate) {
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(switchKind);
      return;
    }
    switchKind();
  };

  const handleUserTypeFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleKindChange(e.target.value === 'mobile' ? 'mobile' : 'web');
  };

  // Dropdown fasilitas kesehatan (GET /map/facility/location layer healthcare -
  // endpoint existing yang org-scoped). User Type Mobile: ID akun =
  // health_care_facility_id. User Type Web: rumah sakit yang ditautkan ke akun
  // web (opsional).
  const { data: facilityLocationData } = useQuery({
    queryKey: ['healthcareFacilityOptions'],
    queryFn: () => smartCityApi.getMapFacilityLocation(),
    staleTime: 5 * 60 * 1000,
  });
  const facilityOptions = (facilityLocationData?.data?.healthcare?.locations ?? []).map((f) => ({
    value: String(f.id),
    label: `${f.id} - ${f.name ?? ''}`,
  }));
  const webAccountTypeOptions = [
    { value: 'internal', label: t('user > account type general') },
    { value: 'hospital', label: t('user > account type hospital') },
  ];
  const handleWebAccountTypeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const next = e.target.value === 'hospital' ? 'hospital' : 'internal';
    setWebAccountType(next);
    // Internal = tanpa rumah sakit: kosongkan supaya tautan lama ikut dilepas.
    if (next === 'internal') setFormValues((prev) => ({ ...prev, health_care_facility_id: '' }));
    if (isNewClickedRef.current) setHasChangesCreate(true);
    else setHasChangesUpdate(true);
    if (selectedUserRef.current?.id) setHasChangesUpdate(true);
  };
  // Yang dikirim ke backend: id fasilitas untuk akun rumah sakit, '' (lepas
  // tautan) untuk akun internal.
  const facilityIdForRequest = () =>
    webAccountType === 'hospital' && formValues.health_care_facility_id != null
      ? String(formValues.health_care_facility_id)
      : '';
  const { data: userData, error: userError } = useUser(selectedUser?.id ?? '');
  const createUserMutation = useCreateUser((responseData?: { id: string }) => {
    createCallback();
    const newUserId = responseData?.id ?? null;
    setNewId(newUserId);
  });
  const updateUserMutation = useUpdateUser(() => {
    updateCallback();
  });
  const deleteUserMutation = useDeleteUser(() => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    setIsRequired(false);
    emptyDetail();
    setDisabledForm(true);
    setDisabledId(true);
    setHasChangesUpdate(false);
    setSelectedUser({ id: null });
    setNewId(null);
  });

  const { commonListData } = useCommonCodes({
    optionParams,
  });

  const optionsRadioFilter = commonListData?.['001']
    ? [
        { value: 'All', label: t('cmn > all'), code: 'All' },
        ...(commonListData['001']?.code?.map((code, index) => ({
          value: code ?? '',
          label: commonListData['001']?.name[index] ?? '',
          code: code ?? '',
        })) || []),
      ]
    : [];

  // Tabulator options
  const languageTabulator = () => ({
    pagination: {
      first: t('cmn > first page'),
      first_title: t('cmn > first page'),
      last: t('cmn > last page'),
      last_title: t('cmn > last page'),
      prev: t('cmn > page before'),
      prev_title: t('cmn > page before'),
      next: t('cmn > next page'),
      next_title: t('cmn > next page'),
    },
  });

  const optionsTabulator = {
    debugInvalidOptions: true,
    pagination: true,
    movableRows: false,
    resizableRows: false,
    index: 'id',
    locale: 'ko',
    langs: { ko: languageTabulator() },
    paginationSize: 10,
    selectableRows: false,
    rowHeight: 41,
    initialSort: userKind === 'mobile' ? [{ column: 'account_id', dir: 'asc' as const }] : [],
    footerElement: `<div id="footer-bottom" style="padding: 0 20px 0 0; text-align: right;">${t(
      'cmn > total'
    )} ${displayedUsers.length} ${t('cmn > results')}</div>`,
    selectableRowsCheck: (row: Tabulator.RowComponent) =>
      !row.getElement().classList.contains('tabulator-selected'),
    placeholder: `<div style='text-align:center;'>${t('msg > no data')}</div>`,
  };

  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
  }, []);

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    isNewClickedRef.current = isNewClicked;
    selectedUserRef.current = selectedUser;
  }, [hasChangesUpdate, hasChangesCreate, isNewClicked, selectedUser]);

  useEffect(() => {
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    if (userError) new NoticeMessage(t('msg > load data fail'));
  }, [userError, t]);

  useEffect(() => {
    if (!userData) return;
    if (userData) {
      setFormValues({
        id: userData.id,
        account_id: userData.account_id,
        email: userData.email,
        phone_no: userData.phone_no,
        name: userData.name,
        organization: userData.organization,
        birth: userData.birth,
        position: userData.position,
        health_care_facility_id:
          userData.health_care_facility_id == null ? '' : String(userData.health_care_facility_id),
      } as Partial<UserRequest>);
      setWebAccountType(userData.health_care_facility_id == null ? 'internal' : 'hospital');
    }
  }, [userData]);

  // Handlers
  const handleRowSelected = (row: Tabulator.RowComponent) => {
    // Handle selecting a row from the table, update form and states accordingly
    const rowData: User = row.getData();

    if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message
        .confirmClicked()
        .then(() => {
          tbRef.current?.deselectRow();
          row.select();
          row.getElement().classList.add('custom-hover');

          setSelectedUser({ id: rowData.id });
          setDisabledForm(false);
          // ID ikut bisa diedit saat update - backend mendukung rename lewat
          // new_account_id (lihat handleConfirmButtonClick).
          setDisabledId(false);
          setHasChangesUpdate(false);
          setHasChangesCreate(false);
          setIsNewClicked(false);

          requestAnimationFrame(() => {
            nameRef.current?.focus();
          });
        })
        .catch(() => {
          row.deselect();
          const prevId = selectedUserRef.current?.id;
          if (prevId) {
            const prevRow = tbRef.current?.getRow(prevId) || null;
            if (prevRow) {
              prevRow.select();
              prevRow.getElement().classList.add('custom-hover');
            }
          }
        });
    } else {
      tbRef.current?.deselectRow();
      row.select();
      row.getElement().classList.add('custom-hover');

      setSelectedUser({ id: rowData.id });
      setDisabledForm(false);
      setDisabledId(false);
      enableUpdateButtons();
      setIsNewClicked(false);

      requestAnimationFrame(() => {
        nameRef.current?.focus();
      });
    }
  };

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;

      setFormValues((prev) => ({ ...prev, [name]: value }));

      if (isNewClickedRef.current) setHasChangesCreate(true);
      else setHasChangesUpdate(true);

      if (selectedUserRef.current?.id) setHasChangesUpdate(true);
    },
    []
  );

  // Varian untuk komponen yang memberi nilai langsung (SearchSelect), bukan event.
  const handleFieldValueChange = useCallback((name: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));

    if (isNewClickedRef.current) setHasChangesCreate(true);
    else setHasChangesUpdate(true);

    if (selectedUserRef.current?.id) setHasChangesUpdate(true);
  }, []);

  const handleSearch = useCallback(
    (inputVal: string | null = null, radioVal: string | null = null) => {
      // Update query parameters when search or filter changes
      const params: Params = {};

      if (inputVal) {
        params.input = inputVal;
      }

      if (radioVal && radioVal !== 'All') {
        params.deletion = radioVal;
      } else {
        params.deletion = '001002';
      }

      setQueryParams(params);
    },
    []
  );

  const handleNewButtonClick = () => {
    // Handle clicking "New" button: clear form and prepare for user creation
    if (hasChangesCreate || hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        setIsRequired(true);
        tbRef.current?.deselectRow();
        emptyDetail();
        setWebAccountType('internal');
        setIsNewClicked(true);
        setDisabledId(false);
        enableRegisterButtons();
        setSelectedUser({ id: null });
        setDisabledForm(false);
        setHasChangesUpdate(false);
        requestAnimationFrame(() => idRef.current?.focus());
      });
    } else {
      tbRef.current?.deselectRow();
      setIsRequired(true);
      emptyDetail();
      setWebAccountType('internal');
      setIsNewClicked(true);
      setDisabledId(false);
      enableRegisterButtons();
      setSelectedUser({ id: null });
      setDisabledForm(false);
      setHasChangesUpdate(false);
      requestAnimationFrame(() => idRef.current?.focus());
    }
  };

  const omitKeys = <T extends Record<string, any>>(obj: T, keys: (keyof T)[]) => {
    const clone = { ...obj };
    for (const key of keys) {
      delete clone[key];
    }
    return clone;
  };

  const handleRegistButtonClick = () => {
    // id/new_account_id adalah field internal (sisa dari baris yang pernah
    // dipilih, dikosongkan emptyDetail) - bukan isian form, jangan divalidasi.
    const fieldsToCheck = omitKeys(formValues, [
      'email',
      'position',
      'phone_no',
      'id',
      'new_account_id',
      'health_care_facility_id',
    ]);

    const isEmptyField = Object.values(fieldsToCheck).some(
      (value) => value === null || value === ''
    );

    if (isEmptyField) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('errmsg > required'));
      return;
    }

    // Jenis akun mengikuti filter User Type: Mobile = ID numerik (health_care_
    // facility_id), Web = ID string. Salah bentuk = akun nyasar ke daftar
    // jenis yang lain.
    if (userKind === 'mobile' && !isMobileAccount(formValues.account_id)) {
      new NoticeMessage(t('msg > invalid mobile account id'));
      return;
    }
    if (userKind === 'web' && isMobileAccount(formValues.account_id)) {
      new NoticeMessage(t('msg > invalid web account id'));
      return;
    }
    // Akun web rumah sakit wajib memilih fasilitasnya.
    if (
      userKind === 'web' &&
      webAccountType === 'hospital' &&
      !formValues.health_care_facility_id
    ) {
      new NoticeMessage(t('errmsg > required'));
      return;
    }

    // Submit new user registration with hashed password
    const data: UserRequest = {
      ...formValues,
      no_hash_password: formValues?.password ?? '',
      password: Common.SHA256(formValues?.password ?? ''),
      birth: dayjs(formValues?.birth).format('DD/MM/YYYY'),
      health_care_facility_id: facilityIdForRequest(),
    };
    createUserMutation.mutateAsync(data);
  };
  const handleConfirmButtonClick = () => {
    // birth ikut opsional: user lama tidak punya date_of_birth, dan sebelum
    // ini update SELALU terblokir untuk mereka ("필수 값을 모두 입력해주세요").
    const fieldsToCheck = omitKeys(formValues, [
      'email',
      'password',
      'confirmation',
      'position',
      'phone_no',
      'birth',
      'health_care_facility_id',
    ]);

    const isEmptyField = Object.values(fieldsToCheck).some(
      (value) => value === null || value === ''
    );

    if (isEmptyField) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('errmsg > required'));
      return;
    }
    // Rename tidak boleh menyeberangkan jenis akun (web <-> mobile) - itu
    // sama saja memindahkan akun ke User Type lain secara tidak sengaja.
    const originalAccountId = userData?.account_id ?? formValues.account_id;
    if (isMobileAccount(originalAccountId) !== isMobileAccount(formValues.account_id)) {
      new NoticeMessage(t('msg > invalid account type change'));
      return;
    }
    // Akun web rumah sakit wajib memilih fasilitasnya.
    if (
      userKind === 'web' &&
      webAccountType === 'hospital' &&
      !formValues.health_care_facility_id
    ) {
      new NoticeMessage(t('errmsg > required'));
      return;
    }
    // Confirm and submit user update with hashed password.
    // account_id = ID ASLI (kunci baris di backend); new_account_id = isi
    // field ID sekarang - backend me-rename bila berbeda.
    const data: UserRequest = {
      ...formValues,
      account_id: userData?.account_id ?? formValues.account_id,
      new_account_id: formValues.account_id,
      no_hash_password: formValues?.password ?? '',
      password: Common.SHA256(formValues?.password ?? ''),
      birth: formValues?.birth ? dayjs(formValues.birth).format('MM/DD/YYYY') : '',
      health_care_facility_id: facilityIdForRequest(),
    };
    updateUserMutation.mutateAsync(data);
  };

  const handleCancelButtonClick = () => {
    // Handle cancel action depending on whether editing or creating
    if (hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        setDisabledId(true);
        enableInitialButtons();
        setHasChangesUpdate(false);
      });
      return;
    }

    if (isNewClicked) {
      if (hasChangesCreate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message.confirmClicked().then(() => {
          setIsRequired(false);
          emptyDetail();
          setIsNewClicked(false);
          setDisabledForm(true);
          setDisabledId(true);
          enableInitialButtons();
          setHasChangesCreate(false);
        });
      } else {
        setIsRequired(false);
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        setDisabledId(true);
        enableInitialButtons();
        setHasChangesCreate(false);
      }
      return;
    }

    reloadCallback();
  };

  const handleDeleteButtonClick = () => {
    // Confirm and delete the currently selected user
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('msg > delete confirm'), {
      mode: 'confirm',
    });
    message.confirmClicked().then(() => {
      deleteUserMutation.mutateAsync(selectedUser?.id ?? '');
    });
  };

  useEffect(() => {
    let locale: Partial<import('air-datepicker').AirDatepickerLocale> = localeKo;
    if (i18n.language === 'eng') locale = localeEn;
    else if (i18n.language === 'ind') locale = localeId;

    const datepicker = new AirDatepicker('[name="birth"]', {
      autoClose: true,
      locale,
      // position: 'top center' as unknown as AirDatepickerPosition,
      position: zoomSafeDatepickerPosition('top center'),
      onSelect: (date: { formattedDate: string | string[] }) => {
        const formatted = Array.isArray(date.formattedDate)
          ? (date.formattedDate[0] ?? '')
          : date.formattedDate;
        handleInputChange({
          target: {
            name: 'birth',
            value: dayjs(formatted).format('YYYY-MM-DD'),
          },
        } as unknown as React.ChangeEvent<HTMLInputElement>);
        setFormValues((prev) => ({
          ...prev,
          birth: dayjs(formatted).format('YYYY-MM-DD'),
        }));
      },
    });

    return () => datepicker.destroy();
  }, [i18n.language, handleInputChange]);

  //Logs
  const logs = userData
    ? [
        {
          label: t('cmn > registered by'),
          value: userData.registered_by ?? '',
        },
        {
          label: t('cmn > registered time'),
          value: userData.registered_time
            ? formatFullDateTimeDDMMYYYY(new Date(userData.registered_time))
            : '',
        },
        { label: t('cmn > updated by'), value: userData.updated_by ?? '' },
        {
          label: t('cmn > updated time'),
          value: userData.updated_time
            ? formatFullDateTimeDDMMYYYY(new Date(userData.updated_time))
            : '',
        },
      ]
    : [];

  return (
    <>
      <section className="wrap">
        <div className="header-title">
          <h3>{t('SYSTEM')}</h3>
          <h3>&gt;</h3>
          <h3>{t('SYSTEM-USER')}</h3>
        </div>

        <ContainerCard>
          <Filtering
            searchRef={searchRef}
            placeholder={
              t('cmn > id') +
              ' / ' +
              t('user > name') +
              ' / ' +
              t('user > email') +
              ' / ' +
              t('user > phone no')
            }
            onSearch={handleSearch}
            onReset={() => {
              // Reset = semua filter kembali ke default, termasuk User Type.
              handleKindChange('web');
              setAccountTypeFilter('All');
            }}
            optionsRadioFilter={optionsRadioFilter}
            labelSelect={t('user > user type')}
          >
            <Select
              value={userKind}
              onChange={handleUserTypeFilterChange}
              options={userTypeOptions}
            />
          </Filtering>
        </ContainerCard>
      </section>

      <ContainerCard>
        {/* User Type Website: Account Type sebagai tab, gaya sama dengan tab
            Web/Mobile sebelumnya. Mobile tidak punya jenis lain - tabel penuh. */}
        {userKind === 'web' && (
          <div className="flex items-end gap-1 border-b border-gray-200 px-2 mb-3">
            {accountTypeTabs.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setAccountTypeFilter(tab.value)}
                className={`-mb-px flex items-center gap-2 rounded-t-lg px-5 py-3 border-b-[3px] title2 cursor-pointer transition-colors ${
                  accountTypeFilter === tab.value
                    ? 'text-text-blue-100 border-b-text-blue-100 bg-blue-50 font-semibold'
                    : 'border-b-transparent text-db-text hover:bg-gray-50 hover:text-text-blue-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
        <ReactTabulator
          id="user-management-table"
          className="container-tabullator"
          data={displayedUsers}
          columns={columnsHistory as unknown as ColumnDefinition[]}
          layout={'fitDataStretch'}
          options={optionsTabulator}
          onRef={(r: { current: Tabulator | null }) => {
            tbRef.current = r.current;
          }}
          events={{
            rowClick: (_e: UIEvent, row: Tabulator.RowComponent) => handleRowSelected(row),
            tableBuilt: () => {
              if (selectedUser?.id) {
                const row = tbRef.current?.getRow(selectedUser.id) || null;
                // getRow mengembalikan FALSE (bukan undefined) bila id tidak
                // ada di daftar yang sedang tampil - optional chaining tidak
                // menahannya, jadi wajib guard truthy.
                if (row) {
                  row.select();
                  row.getElement().classList.add('custom-hover');
                }
              } else if (newId) {
                const row = tbRef.current?.getRow(newId) || null;
                if (row) {
                  tbRef.current?.scrollToRow(row, 'bottom', true);
                  row.select();
                  row.getElement().classList.add('custom-hover');
                  tbRef.current?.selectRow([newId]);
                  handleRowSelected(row);
                }
              }
            },
          }}
        />
      </ContainerCard>

      <ContainerCard>
        <div className="flex w-full flex-col gap-[10px]">
          <div className="grid grid-cols-3 gap-4">
            <div className="flex flex-col gap-4">
              {/* Jenis akun yang sedang diisi mengikuti filter User Type di atas.
                  Ditampilkan terkunci supaya jelas New membuat akun Web atau
                  Mobile - tanpa tab, form tidak punya petunjuk lain. */}
              <DetailForm
                label={t('user > user type')}
                value={userKind}
                inputType={'select'}
                name={'user_type'}
                disabled
                optionSelect={userTypeOptions}
              />
              {userKind === 'mobile' ? (
                // Akun mobile: ID = health_care_facility_id, dipilih dari
                // dropdown fasilitas kesehatan yang bisa dicari (100+ fasilitas).
                <DetailForm label={t('user > health care facility id')} required showInput={false}>
                  <SearchSelect
                    name="account_id"
                    value={formValues.account_id || ''}
                    options={facilityOptions}
                    placeholder={t('cm > select db')}
                    disabled={disabledId}
                    onChange={(value) => handleFieldValueChange('account_id', value)}
                    variant="form"
                  />
                </DetailForm>
              ) : (
                <>
                  <DetailForm
                    label={t('profile > id')}
                    value={formValues.account_id || ''}
                    inputType={'text'}
                    onChange={handleInputChange}
                    name={'account_id'}
                    disabled={disabledId}
                    required
                    formRef={idRef}
                  />
                  {/* Jenis akun web: General (dinas, Dishub, super user / root) tanpa rumah
                      sakit; Rumah Sakit wajib memilih fasilitas yang ditautkan
                      (health_care_facility.user_id_website). */}
                  <DetailForm
                    label={t('user > account type')}
                    value={webAccountType}
                    inputType={'select'}
                    onChange={handleWebAccountTypeChange}
                    name={'web_account_type'}
                    disabled={disabledForm}
                    required
                    optionSelect={webAccountTypeOptions}
                  />
                  {webAccountType === 'hospital' && (
                    <DetailForm
                      label={t('user > health care facility id')}
                      required
                      showInput={false}
                    >
                      <SearchSelect
                        name="health_care_facility_id"
                        value={
                          formValues.health_care_facility_id == null
                            ? ''
                            : String(formValues.health_care_facility_id)
                        }
                        options={facilityOptions}
                        placeholder={t('cm > select db')}
                        disabled={disabledForm}
                        onChange={(value) =>
                          handleFieldValueChange('health_care_facility_id', value)
                        }
                        variant="form"
                      />
                    </DetailForm>
                  )}
                </>
              )}
              <DetailForm
                label={t('profile > name')}
                value={formValues.name || ''}
                inputType={'text'}
                onChange={handleInputChange}
                name={'name'}
                disabled={disabledForm}
                required
                formRef={nameRef}
              />
              <DetailForm
                label={t('profile > birth')}
                value={formValues.birth || ''}
                inputType={'text'}
                onChange={handleInputChange}
                name={'birth'}
                disabled={disabledForm}
                required
                formRef={birthRef}
              />
            </div>

            <div className="flex flex-col gap-4">
              <DetailForm
                label={t('profile > email')}
                value={formValues.email || ''}
                inputType={'text'}
                onChange={handleInputChange}
                name={'email'}
                disabled={disabledForm}
              />
              <DetailForm
                label={t('profile > new password')}
                value={formValues.password || ''}
                inputType={'password'}
                onChange={handleInputChange}
                name={'password'}
                maxLength={20}
                disabled={disabledForm}
                required={isRequired}
              />
              <DetailForm
                label={t('profile > confirm password')}
                value={formValues.confirmation || ''}
                inputType={'password'}
                onChange={handleInputChange}
                name={'confirmation'}
                disabled={disabledForm}
                required={isRequired}
                maxLength={20}
              />
            </div>

            <div className="flex flex-col gap-4">
              <DetailForm
                label={t('profile > phone')}
                value={formValues.phone_no || ''}
                inputType={'text'}
                pattern={'^[0-9]*$'}
                onChange={handleInputChange}
                name={'phone_no'}
                disabled={disabledForm}
              />

              <DetailForm
                label={t('user > organization')}
                value={formValues.organization || ''}
                inputType={'select'}
                onChange={handleInputChange}
                name={'organization'}
                disabled={disabledForm}
                required
                optionSelect={
                  commonListData?.ORG
                    ? [
                        ...(commonListData.ORG?.code?.map((code, index) => ({
                          value: code ?? '',
                          label: commonListData.ORG?.name[index] ?? '',
                        })) || []),
                      ]
                    : []
                }
              />

              <DetailForm
                label={t('021')}
                value={formValues.position || ''}
                inputType={'select'}
                onChange={handleInputChange}
                name={'position'}
                disabled={disabledForm}
                optionSelect={
                  commonListData?.['021']
                    ? [
                        ...(commonListData['021']?.code?.map((code, index) => ({
                          value: code ?? '',
                          label: commonListData['021']?.name[index] ?? '',
                        })) || []),
                      ]
                    : []
                }
              />
            </div>
          </div>

          <hr className="border-t border-gray-300" />

          <div className="flex items-center justify-between gap-4 w-full">
            <div className="flex-1">{disabledForm ? null : <LogList logs={logs} />}</div>
            <div className="flex-none">
              <ButtonGroup
                onClickDelete={handleDeleteButtonClick}
                onClickNew={handleNewButtonClick}
                onClickCancel={handleCancelButtonClick}
                onClickRegist={handleRegistButtonClick}
                onClickConfirm={handleConfirmButtonClick}
                isNewClicked={isNewClicked}
                cancelButtonState={buttonState.cancel}
                confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
                deleteButtonState={buttonState.delete}
                newButtonState={hasChangesCreate ? false : buttonState.create}
              />
            </div>
          </div>
        </div>
      </ContainerCard>
    </>
  );
};

export default UserManagement;
